import React, { useCallback, useEffect, useRef, useState } from "react";
import Echo from "laravel-echo";
import { Dialog, Box, Typography, IconButton, Chip } from "@mui/material";
import { NotificationsActive, Close, TableRestaurant } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import NotificationSound from "../assets/sound/noti.mp3";

let callSeq = 0;

// Global, always-mounted (see App.js) — a guest's "Call Waiter" tap has to
// reach whichever screen the waiter is on, not just a Home/Orders tab, so
// this lives above the router rather than inside MainLayout.
export default function WaiterCallAlert() {
  const { token, store } = useAuth();
  const [queue, setQueue] = useState([]); // [{ id, tableName, calledAt }]
  const audioRef = useRef(null);

  const active = queue[0] || null;

  useEffect(() => {
    if (!token || !store?.id) return;

    // Same Echo/socket.io setup denzo-pos's App.js uses for pos.orders.{storeId}
    // (order events) — this app didn't have a live connection of its own yet,
    // so a call from the self-order app while a waiter is mid-screen needs one.
    window.io = require("socket.io-client");
    const echo = new Echo({
      broadcaster: "socket.io",
      host: process.env.REACT_APP_SOCKET_URL,
      client: require("socket.io-client"),
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      },
    });

    const channelName = "pos.orders." + store.id;
    echo.private(channelName).listen("WaiterCalled", (e) => {
      callSeq += 1;
      setQueue((q) => [...q, { id: callSeq, tableName: e.table?.name || "?", calledAt: Date.now() }]);
    });

    return () => {
      echo.leave(channelName);
      echo.disconnect();
    };
  }, [token, store?.id]);

  // Browsers (and the native WebView shell — see denzo-waiter-app/App.js's
  // mediaPlaybackRequiresUserAction) refuse to run audio.play() unless it's
  // called from an actual user gesture — the call alert has none behind it,
  // since it's triggered by a socket event, not a tap. So the very first
  // tap/click anywhere on the page "unlocks" this audio element with a
  // muted play+pause; browsers then keep allowing that same element to play
  // programmatically for the rest of the page's life.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const unlock = () => {
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        })
        .catch(() => {
          audio.muted = false;
        });
    };

    document.addEventListener("pointerdown", unlock, { once: true, capture: true });
    return () => document.removeEventListener("pointerdown", unlock, { capture: true });
  }, []);

  // Rings on loop for as long as there's an unacknowledged call, and stops
  // the instant the popup is dismissed — a single missed glance shouldn't
  // mean a missed table.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (active) {
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [active]);

  const dismiss = useCallback(() => {
    setQueue((q) => q.slice(1));
  }, []);

  return (
    <>
      <audio ref={audioRef} src={NotificationSound} loop />

      <Dialog
        open={!!active}
        onClose={dismiss}
        onClick={dismiss}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { textAlign: "center", py: 4, px: 3, cursor: "pointer", borderRadius: 3 } }}
      >
        <IconButton
          onClick={dismiss}
          size="small"
          sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Box
          sx={{
            mx: "auto",
            mb: 2,
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "denzo-waiter-call-pulse 1s ease-in-out infinite",
            "@keyframes denzo-waiter-call-pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(198,40,40,0.5)" },
              "70%": { boxShadow: "0 0 0 18px rgba(198,40,40,0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(198,40,40,0)" },
            },
          }}
        >
          <NotificationsActive fontSize="large" />
        </Box>

        <Typography variant="h6" fontWeight={800}>
          Calling for assistance...
        </Typography>

        {active && (
          <Chip
            icon={<TableRestaurant fontSize="small" />}
            label={`Table ${active.tableName}`}
            color="primary"
            sx={{ mt: 1.5, fontWeight: 700 }}
          />
        )}

        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Tap anywhere to acknowledge and stop the alert.
        </Typography>

        {queue.length > 1 && (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            +{queue.length - 1} more call{queue.length - 1 > 1 ? "s" : ""} waiting
          </Typography>
        )}
      </Dialog>
    </>
  );
}
